import React, { useMemo, useState, useRef } from 'react';
import { formatDate, formatDateTime, formatTime as formatClockTime } from '../shared/dateTimeFormatting';
import { useDateTimePreferences } from '../shared/DateTimeProvider';
import Timeline, { TimelineHeaders, DateHeader, SidebarHeader, TimelineMarkers, TodayMarker } from 'react-calendar-timeline';
import 'react-calendar-timeline/style.css';
import { timelineSourceBackground, timelineSourceBorder } from './timelineSources';

export interface LinearTimelineEntry {
  rotationId: string;
  rotationName: string;
  startUtc: string;
  endUtc: string;
  userId: string;
  userName: string;
  source?: string;
  sourceId?: string | null;
  /** Human-readable description of the source, e.g. "E covering B – sick leave". */
  sourceLabel?: string;
}

interface Props {
  entries: LinearTimelineEntry[];
  timezone: string;
  spanDays?: Span;
  onSpanDaysChange?: (span: Span) => void;
  showControls?: boolean;
  defaultSpanDays?: Span;
  lockViewport?: boolean;
  selectedEntryKey?: string | null;
  onEntrySelect?: (entry: LinearTimelineEntry) => void;
}

type Span = 1 | 7 | 14 | 30;
const DAY_MS = 24 * 60 * 60 * 1000;

export const ScheduleLinearTimeline: React.FC<Props> = ({
  entries,
  timezone,
  spanDays,
  onSpanDaysChange,
  showControls = true,
  defaultSpanDays = 7,
  lockViewport = false,
  selectedEntryKey = null,
  onEntrySelect,
}) => {
  const preferences = useDateTimePreferences();
  const [probe, setProbe] = useState<{ groupId: string; time: number } | null>(null);
  const [internalSpanDays, setInternalSpanDays] = useState<Span>(defaultSpanDays);
  const effectiveSpanDays = spanDays ?? internalSpanDays;

  // Track scroll offset from the default anchor (local midnight today).
  // When span changes, offset resets to 0 so the window snaps back to anchor.
  const [scrollOffset, setScrollOffset] = useState(0);
  const prevSpanRef = useRef(effectiveSpanDays);
  if (prevSpanRef.current !== effectiveSpanDays) {
    prevSpanRef.current = effectiveSpanDays;
    // Reset scroll offset synchronously on span change (no useEffect lag)
    if (scrollOffset !== 0) setScrollOffset(0);
  }

  const updateSpanDays = (next: Span) => {
    if (spanDays === undefined) setInternalSpanDays(next);
    onSpanDaysChange?.(next);
  };

  const now = useMemo(() => Date.now(), []);
  const anchorStart = useMemo(() => {
    const d = alignToLocalMidnightUtc(new Date(now), timezone || 'UTC');
    return d.getTime();
  }, [now, timezone]);

  const windowStart = anchorStart + scrollOffset;
  const windowEnd = windowStart + effectiveSpanDays * DAY_MS;

  // Keep item filtering consistent with the computed window
  const visibleStart = windowStart;
  const visibleEnd = windowEnd;

  const groups = useMemo((() => {
    const map = new Map<string, { id: string; title: string }>();
    entries.forEach((e) => {
      if (!map.has(e.rotationId)) {
        map.set(e.rotationId, { id: e.rotationId, title: e.rotationName });
      }
    });
    return Array.from(map.values()).sort((a, b) =>
      (a.title || '').localeCompare(b.title || '', undefined, { sensitivity: 'base' })
    );
  }), [entries]);

  const items = useMemo((() => {
    const userIds = Array.from(new Set(entries.map((e) => e.userId))).sort((a, b) => a.localeCompare(b));
    const colorByUser = new Map<string, string>();
    userIds.forEach((userId, index) => {
      const hue = Math.round((index * 137.508) % 360); // golden-angle spread
      colorByUser.set(userId, `hsl(${hue} 70% 47%)`);
    });

    const colorFor = (userId: string) => colorByUser.get(userId) || 'var(--color-primary)';

    return entries
      .filter((e) => {
        const s = new Date(e.startUtc).getTime();
        const en = new Date(e.endUtc).getTime();
        return en > visibleStart && s < visibleEnd;
      })
      .map((e) => {
        const start = new Date(e.startUtc).getTime();
        const end = new Date(e.endUtc).getTime();
        const color = colorFor(e.userId);
        const tooltip = `${e.userName} | ${formatTimeRange(start, end, timezone)} | ${e.sourceLabel || e.source || 'roster'}`;
        return {
          id: toEntryKey(e),
          group: e.rotationId,
          title: `${e.userName}`,
          tooltip,
          start_time: start,
          end_time: end,
          entry: e,
          canMove: false,
          canResize: false,
          canChangeGroup: false,
          itemProps: {
            style: {
              background: timelineSourceBackground(e.source, color),
              border: timelineSourceBorder(e.source),
              color: '#fff',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 600,
            },
          },
        };
      });
  }), [entries, visibleStart, visibleEnd, timezone, preferences.locale, preferences.hourCycle]);

  return (
    <div className="schedule-linear-widget">
      {showControls && (
        <div className="linear-toolbar">
          <button className={effectiveSpanDays === 1 ? 'week-btn active' : 'week-btn'} onClick={() => updateSpanDays(1)}>1D</button>
          <button className={effectiveSpanDays === 7 ? 'week-btn active' : 'week-btn'} onClick={() => updateSpanDays(7)}>1W</button>
          <button className={effectiveSpanDays === 14 ? 'week-btn active' : 'week-btn'} onClick={() => updateSpanDays(14)}>2W</button>
          <button className={effectiveSpanDays === 30 ? 'week-btn active' : 'week-btn'} onClick={() => updateSpanDays(30)}>1M</button>
        </div>
      )}

      <Timeline
        groups={groups}
        items={items}
        visibleTimeStart={visibleStart}
        visibleTimeEnd={visibleEnd}
        onTimeChange={(start: number, _end: number, updateScrollCanvas: (canvasTimeStart: number, canvasTimeEnd: number) => void) => {
          if (lockViewport) {
            updateScrollCanvas(visibleStart, visibleEnd);
            return;
          }
          const targetWidth = effectiveSpanDays * DAY_MS;
          const normalizedEnd = start + targetWidth;
          setScrollOffset(start - anchorStart);
          updateScrollCanvas(start, normalizedEnd);
        }}
        keys={{
          groupIdKey: 'id',
          groupTitleKey: 'title',
          groupLabelKey: 'title',
          groupRightTitleKey: 'title',
          itemIdKey: 'id',
          itemTitleKey: 'title',
          itemDivTitleKey: 'tooltip',
          itemGroupKey: 'group',
          itemTimeStartKey: 'start_time',
          itemTimeEndKey: 'end_time',
        }}
        canMove={false}
        canResize={false}
        stackItems={true}
        lineHeight={42}
        itemHeightRatio={0.62}
        sidebarWidth={180}
        timeSteps={{ second: 1, minute: 1, hour: 1, day: 1, month: 1, year: 1 }}
        minZoom={lockViewport ? effectiveSpanDays * DAY_MS : undefined}
        maxZoom={lockViewport ? effectiveSpanDays * DAY_MS : undefined}
        minResizeWidth={0}
        itemRenderer={({ item, itemContext, getItemProps }: any) => {
          const fullName = String(item?.title || '');
          const widthPx = Number(itemContext?.dimensions?.width || 0);
          const isLongRange = effectiveSpanDays === 14 || effectiveSpanDays === 30;
          const estimatedLabelWidth = (fullName.length * 7) + 14;
          const initials = toInitials(fullName);
          const displayName = isLongRange && widthPx > 0 && estimatedLabelWidth > widthPx
            ? initials
            : fullName;

          return (
            <div
              {...getItemProps({
                style: {
                  background: item.itemProps?.style?.background,
                  border: item.id === selectedEntryKey
                    ? '2px solid #22d3ee'
                    : item.itemProps?.style?.border,
                  color: item.itemProps?.style?.color,
                  borderRadius: item.itemProps?.style?.borderRadius,
                  fontSize: item.itemProps?.style?.fontSize,
                  fontWeight: item.itemProps?.style?.fontWeight,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
              })}
              title={item.tooltip}
            >
              <div style={{ padding: '0 6px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {displayName}
              </div>
            </div>
          );
        }}
        onCanvasClick={(groupId: string | number, time: number) => {
          setProbe({ groupId: String(groupId), time });
        }}
        onItemSelect={(itemId: number | string) => {
          if (!onEntrySelect) return;
          const selected = items.find((it: any) => String(it.id) === String(itemId));
          if (selected?.entry) onEntrySelect(selected.entry as LinearTimelineEntry);
        }}
      >
        <TimelineMarkers>
          <TodayMarker>
            {({ styles }: { styles: React.CSSProperties }) => <div className="schedule-now-marker" title="Current time" aria-label="Current time"
              style={{ ...styles, backgroundColor: 'var(--color-danger, #dc2626)', width: 2, pointerEvents: 'none' }} />}
          </TodayMarker>
        </TimelineMarkers>
        <TimelineHeaders className="rct-dark-headers" calendarHeaderClassName="rct-dark-calendar-header">
          <SidebarHeader>
            {({ getRootProps }: { getRootProps: () => Record<string, unknown> }) => (
              <div {...getRootProps()} className="rct-sidebar-title">Rotation</div>
            )}
          </SidebarHeader>
          <DateHeader
            unit={effectiveSpanDays === 1 ? 'day' : 'month'}
            labelFormat={(value: any) => {
              const [start] = value as any[];
              if (effectiveSpanDays === 1) return formatDate(start.valueOf(), { timeZone: timezone, weekday: 'short', day: 'numeric', month: 'short' });
              return formatDate(start.valueOf(), { timeZone: timezone, month: 'long', year: 'numeric' });
            }}
          />
          <DateHeader
            unit={effectiveSpanDays === 1 ? 'hour' : 'day'}
            labelFormat={(value: any, _unit: any, labelWidth?: number) => {
              const [start] = value as any[];
              if (effectiveSpanDays === 1) return formatClockTime(start.valueOf(), { timeZone: timezone, hour: 'numeric' });
              if (effectiveSpanDays === 7) return formatDate(start.valueOf(), { timeZone: timezone, weekday: 'short', day: 'numeric', month: 'numeric' });
              if (effectiveSpanDays === 14) return formatDate(start.valueOf(), { timeZone: timezone, day: 'numeric', ...((labelWidth || 0) < 52 ? {} : { weekday: 'short' as const }) });
              return formatDate(start.valueOf(), { timeZone: timezone, day: 'numeric', ...((labelWidth || 0) < 52 ? {} : { month: 'numeric' as const }) });
            }}
          />
        </TimelineHeaders>
      </Timeline>

      {items.length === 0 && (
        <div className="empty-row" style={{ marginTop: 10 }}>
          No timeline entries for selected range.
        </div>
      )}
      <div className="help-text" style={{ marginTop: 8 }}>
        Times displayed in timezone: {timezone}
        {' · '}The red vertical line marks the current time when it is within the visible range.
      </div>
      {probe && (
        <div className="help-text" style={{ marginTop: 6 }}>
          On-call at {formatTime(probe.time, timezone)}: {formatProbe(entries, groups, probe.groupId, probe.time, timezone)}
        </div>
      )}
      <div className="help-text" style={{ marginTop: 4 }}>
        Hover a block for shift details. Click a row/time to probe exact on-call assignment.
      </div>
    </div>
  );
};

export default ScheduleLinearTimeline;

function toInitials(name: string): string {
  const parts = (name || '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0] || ''}${parts[1][0] || ''}`.toUpperCase();
}

function toEntryKey(entry: LinearTimelineEntry): string {
  return `${entry.rotationId}|${entry.startUtc}|${entry.endUtc}|${entry.userId}`;
}

function toLocalParts(dateUtc: Date, timezone: string): { year: number; month: number; day: number; hour: number; minute: number; second: number } {
  const dtf = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
  const parts = dtf.formatToParts(dateUtc);
  const get = (type: string) => Number(parts.find(p => p.type === type)?.value || 0);
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
    second: get('second'),
  };
}

function localToUtc(localDateLikeUtc: Date, timezone: string): Date {
  const asIfUtc = localDateLikeUtc.getTime();
  const partsInTz = toLocalParts(new Date(asIfUtc), timezone);
  const tzAsUtc = Date.UTC(partsInTz.year, partsInTz.month - 1, partsInTz.day, partsInTz.hour, partsInTz.minute, partsInTz.second);
  const offset = tzAsUtc - asIfUtc;
  return new Date(asIfUtc - offset);
}

function alignToLocalMidnightUtc(referenceUtc: Date, timezone: string): Date {
  const local = toLocalParts(referenceUtc, timezone || 'UTC');
  const localMidnightAsUtcDate = new Date(Date.UTC(local.year, local.month - 1, local.day, 0, 0, 0));
  return localToUtc(localMidnightAsUtcDate, timezone || 'UTC');
}

function formatTime(ms: number, timezone: string): string {
  return formatDateTime(ms, {
    timeZone: timezone || 'UTC',
    weekday: 'short',
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function formatTimeRange(startMs: number, endMs: number, timezone: string): string {
  return `${formatTime(startMs, timezone)} -> ${formatTime(endMs, timezone)}`;
}

function formatProbe(
  entries: LinearTimelineEntry[],
  groups: Array<{ id: string; title: string }>,
  groupId: string,
  time: number,
  timezone: string
): string {
  const group = groups.find(g => g.id === groupId);
  const hit = entries.find(e => e.rotationId === groupId && new Date(e.startUtc).getTime() <= time && new Date(e.endUtc).getTime() > time);
  if (!group) return 'No matching rotation row';
  if (!hit) return `${group.title} unassigned`;

  const start = new Date(hit.startUtc).getTime();
  const end = new Date(hit.endUtc).getTime();
  return `${group.title}: ${hit.userName} (${hit.sourceLabel || hit.source || 'roster'}) [${formatTimeRange(start, end, timezone)}]`;
}
